import { Fragment, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useForm } from 'react-hook-form'

export default function DeleteBoxModal({ isOpen, onClose, box, onDelete }) {
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm()

  const onSubmit = async (data) => {
    try {
      setDeleting(true)
      setError(null)
      await onDelete(data.reason)
      onClose()
    } catch (err) {
      setError(err.message || 'Failed to delete box')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-3xl bg-white p-5 sm:p-6 text-left align-middle shadow-xl transition-all max-h-[82vh] overflow-y-auto">
                <div className="flex items-start justify-between gap-4 mb-6">
                  <div className="flex items-start flex-1">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                    </div>
                    <div className="ml-3 flex-1">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold text-gray-900"
                      >
                        Delete Box
                      </Dialog.Title>
                      <p className="mt-1 text-sm text-gray-500">
                        Are you sure you want to delete <strong>{box?.name}</strong>? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="rounded-full p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-2xl">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div>
                    <label
                      htmlFor="reason"
                      className="block text-base font-semibold text-gray-900 mb-2"
                    >
                      Reason for deletion <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="reason"
                      rows={4}
                      {...register('reason', {
                        required: 'Please provide a reason for deleting this box',
                        minLength: {
                          value: 10,
                          message: 'Reason must be at least 10 characters',
                        },
                      })}
                      className="block w-full px-4 py-3 text-base border border-gray-200 rounded-2xl shadow-sm bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors resize-none"
                      placeholder="Explain why you are deleting this box..."
                    />
                    {errors.reason && (
                      <p className="mt-2 text-sm text-red-600">{errors.reason.message}</p>
                    )}
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4">
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> All members will be notified of this deletion. Valid ledger entries will remain in the system for record-keeping.
                    </p>
                  </div>

                  <div className="flex justify-end space-x-4 pt-4 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-6 py-3 border border-gray-200 rounded-full shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={deleting}
                      className="px-6 py-3 border border-transparent rounded-full shadow-sm text-base font-semibold text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {deleting ? 'Deleting...' : 'Delete Box'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
